import {
  TGetFeatureProps,
  TGetFileTypeProps,
  TGetIdeProps,
} from '@/console/features/pipelines/hooks/use-feature/use-feature.types';
import { useReadTenantQuery } from '@/shared/redux/api/ident/ident-api';
import {
  DeviceTypeEnum,
  FeatureConfigFragment,
  FileTypeFragment,
  IdeFragment,
  ProjectTypeEnum,
  VendorFragment,
} from '@/shared/types/types.generated';

export const useFeatureV2 = () => {
  const { data, isLoading } = useReadTenantQuery();
  const vendors: VendorFragment[] = data?.readTenant.vendors || [];

  const getVendor = (vendorId: string): VendorFragment | undefined => {
    return vendors.find((vendor) => vendor.vendorId === vendorId);
  };

  const getIde = ({
    vendorId,
    ideConfigId,
  }: TGetIdeProps): IdeFragment | undefined => {
    const vendor = getVendor(vendorId);
    if (!vendor?.ides?.length) {
      return undefined;
    }
    return vendor.ides.find((ide) => ide.ideConfigId === ideConfigId);
  };

  const getFeature = ({
    vendorId,
    ideConfigId,
    feature,
    ...props
  }: TGetFeatureProps): FeatureConfigFragment | undefined => {
    const ide = getIde({ vendorId, ideConfigId });

    const features =
      ide?.features?.filter(
        ({ feature: featureId }) => featureId === feature,
      ) ?? [];

    if (props.deviceType) {
      return features.find((feature) =>
        getDeviceTypesForFeature(feature)?.includes(props.deviceType!),
      );
    }

    if (props.projectType) {
      return features.find((feature) =>
        getProjectTypesForFeature(feature)?.includes(props.projectType!),
      );
    }

    return features?.[0];
  };

  const getDeviceTypesForFeature = (
    feature?: FeatureConfigFragment,
  ): DeviceTypeEnum[] => {
    const deviceTypes: DeviceTypeEnum[] = [];

    feature?.metaData?.deviceTypes?.forEach((deviceType) =>
      deviceTypes.push(deviceType),
    );
    feature?.metaData?.fileExtensionsToDeviceTypes
      ?.map(({ deviceType }) => deviceType)
      ?.forEach((deviceType) => deviceTypes.push(deviceType));

    return [...new Set(deviceTypes)];
  };

  const getProjectTypesForFeature = (
    feature?: FeatureConfigFragment,
  ): ProjectTypeEnum[] => {
    const projectTypes: ProjectTypeEnum[] = [];

    feature?.metaData?.fileExtensionsToDeviceTypes?.forEach(({ projectType }) =>
      projectTypes.push(projectType),
    );
    feature?.metaData?.fileToFileExtensions?.forEach(({ projectType }) =>
      projectTypes.push(projectType),
    );
    feature?.metaData?.projectFileExtensions?.forEach(({ projectType }) =>
      projectTypes.push(projectType),
    );

    return [...new Set(projectTypes)];
  };

  const isFeatureEnabled = (props: TGetFeatureProps): boolean => {
    const feature = getFeature(props);
    return !!feature;
  };

  const getFileType = ({
    vendorId,
    ideConfigId,
    projectType,
    extension,
  }: TGetFileTypeProps): FileTypeFragment | undefined => {
    if (!projectType || !vendorId || !ideConfigId) {
      return undefined;
    }

    const ide = getIde({ vendorId, ideConfigId });
    const fileTypes =
      ide?.fileTypes?.filter(
        (fileType) => fileType.projectType === projectType,
      ) ?? [];

    if (fileTypes.length <= 1) {
      return fileTypes[0];
    }

    // Several extensions share this project type; disambiguate by the
    // selected file extension when available.
    const byExtension =
      extension &&
      fileTypes.find((fileType) => fileType.extension === extension);

    if (byExtension) {
      return byExtension;
    }

    // No extension match yet; fall back to the first that defines inputs.
    return (
      fileTypes.find((fileType) => fileType.additionalInputs?.length) ??
      fileTypes[0]
    );
  };

  return {
    isLoading,
    vendors,
    getVendor,
    getIde,
    getFeature,
    getDeviceTypesForFeature,
    getProjectTypesForFeature,
    isFeatureEnabled,
    getFileType,
  };
};
